import {
  Directive,
  ElementRef,
  Input,
  ComponentFactoryResolver,
  ViewContainerRef,
  TemplateRef,
  HostListener, NgZone, ReflectiveInjector, Injector, ComponentFactory, ResolvedReflectiveProvider, ComponentRef
} from '@angular/core';
import { EventEmitter, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';

import {MentionListComponent} from '../common/mention-list/mention-list-component';
import {getCaretPosition, getValue, insertValue, setCaretPosition} from '../common/mention-list/mention-utils';
import {RulesService} from '../rules.service';
import {ThingsService} from '../../things/things.service';
import {SetConditionOption} from '../model/Set-condition.option.service';
import {SetConditionComponent} from '../new-rule/set-condition/set-condition.component';

const KEY_BACKSPACE = 8;
const KEY_TAB = 9;
const KEY_ENTER = 13;
const KEY_SHIFT = 16;
const KEY_ESCAPE = 27;
const KEY_SPACE = 32;
const KEY_LEFT = 37;
const KEY_UP = 38;
const KEY_RIGHT = 39;
const KEY_DOWN = 40;
const KEY_2 = 50;

@Directive({
  selector: '[appThingSelector]',
})
export class ThingSelectorDirective implements OnInit {

  items = [];
  searchString: string;
  startPos: number;
  startNode;
  searchList: ComponentRef<MentionListComponent>;
  stopSearch: boolean;
  iframe: any; // optional
  keyCodeSpecified: boolean;
  selectedItem;
  private token = 'token';

  // template to use for rendering list items
  @Input() mentionListTemplate: TemplateRef<any>;

  // event emitted whenever the search term changes
  @Output() searchTerm = new EventEmitter();

  // the character that will trigger the menu behavior
  private triggerChar: string | number = '{';
  private secondTrigger: string | number = '.';

  // option to specify the field in the objects to be used as the item label
  private labelKey = 'name';

  // option to diable internal filtering. can be used to show the full list returned
  // from an async operation (or allows a custom filter function to be used - in future)
  private disableSearch = false;

  // option to limit the number of items shown in the pop-up menu
  private maxItems: number = -1;

  // optional function to format the selected item before inserting the text
  private mentionSelect: (item: any) => (string) = (item: any) => {
    const token = item.token ? this.token : this.labelKey;
    const value = token !== this.token ? `.attributes.${item[token]}}` : this.triggerChar + item[token];
    this.selectedItem = item;
    return value;
  }



  constructor(
    private _element: ElementRef,
    private _componentResolver: ComponentFactoryResolver,
    private _viewContainerRef: ViewContainerRef,
    private ruleService: RulesService,
    private zone: NgZone,
    private cfr: ComponentFactoryResolver,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnInit() {
    if (this.items && this.items.length > 0) {
      // remove items without an labelKey (as it's required to filter the list)
      this.items = this.items.filter(e => e[this.labelKey]);
      this.items.sort((a, b) => a[this.labelKey].localeCompare(b[this.labelKey]));
      if (this.searchList && this.searchList.instance && !this.searchList.instance.hidden) {
        this.updateSearchList();
      }
    }
  }

  setIframe(iframe: HTMLIFrameElement) {
    this.iframe = iframe;
  }

  stopEvent(event: any) {
    if (!event.wasClick) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  }
  @HostListener('blur', ['$event'])
  blurHandler(event: any) {
    this.stopEvent(event);
    this.stopSearch = true;
    if (this.searchList && this.searchList.instance) {
      this.searchList.instance.hidden = true;
    }
  }
  @HostListener('keydown', ['$event'])
  keyHandler(event: any, nativeElement: HTMLInputElement = this._element.nativeElement) {
    let val: string = getValue(nativeElement);
    let pos = getCaretPosition(nativeElement, this.iframe);
    let charPressed = this.keyCodeSpecified ? event.keyCode : event.key;
    if (!charPressed) {
      let charCode = event.which || event.keyCode;
      if (!event.shiftKey && (charCode >= 65 && charCode <= 90)) {
        charPressed = String.fromCharCode(charCode + 32);
      } else if (event.shiftKey && charCode === KEY_2) {
        charPressed = this.triggerChar;
      } else {
        // TODO (dmacfarlane) fix this for non-alpha keys
        // http://stackoverflow.com/questions/2220196/how-to-decode-character-pressed-from-jquerys-keydowns-event-handler?lq=1
        charPressed = String.fromCharCode(event.which || event.keyCode);
      }
    }
    if (event.keyCode === KEY_ENTER && event.wasClick && pos < this.startPos) {
      // put caret back in position prior to contenteditable menu click
      pos = this.startNode.length;
      setCaretPosition(this.startNode, pos, this.iframe);
    }
    if (charPressed === this.triggerChar || charPressed === this.secondTrigger) {
      this.startPos = pos;
      this.startNode = (this.iframe ? this.iframe.contentWindow.getSelection() : window.getSelection()).anchorNode;
      this.stopSearch = false;
      this.searchString = null;
      this.showSearchList(nativeElement, charPressed);
    } else if (this.startPos >= 0 && !this.stopSearch) {
      if (pos <= this.startPos) {
        this.searchList.instance.hidden = true;
      } else if (event.keyCode !== KEY_SHIFT &&
        !event.metaKey &&
        !event.altKey &&
        !event.ctrlKey &&
        pos > this.startPos
      ) {
        if (event.keyCode === KEY_SPACE) {
          this.startPos = -1;
        } else if (event.keyCode === KEY_BACKSPACE && pos > 0) {
          pos--;
          if (pos === 0) {
            this.stopSearch = true;
          }
          this.searchList.instance.hidden = this.stopSearch;
        } else if (this.searchList && this.searchList.instance && !this.searchList.instance.hidden) {
          if (event.keyCode === KEY_TAB || event.keyCode === KEY_ENTER) {
            this.stopEvent(event);
            this.searchList.instance.hidden = true;
            // value is inserted without a trailing space for consistency
            // between element types (div and iframe do not preserve the space)
            insertValue(nativeElement, this.startPos, pos,
              this.mentionSelect(this.searchList.instance.activeItem), this.iframe);
            // fire input event so angular bindings are updated
            if ('createEvent' in document) {
              let evt = document.createEvent('HTMLEvents');
              evt.initEvent('input', false, true);
              nativeElement.dispatchEvent(evt);
            }
            this.startPos = -1;
            return false;
          } else if (event.keyCode === KEY_ESCAPE) {
            this.stopEvent(event);
            this.searchList.instance.hidden = true;
            this.stopSearch = true;
            return false;
          } else if (event.keyCode === KEY_DOWN) {
            this.stopEvent(event);
            this.searchList.instance.activateNextItem();
            return false;
          } else if (event.keyCode === KEY_UP) {
            this.stopEvent(event);
            this.searchList.instance.activatePreviousItem();
            return false;
          }
        }

        if (event.keyCode === KEY_LEFT || event.keyCode === KEY_RIGHT) {
          this.stopEvent(event);
          return false;
        } else {
          let mention = val.substring(this.startPos + 1, pos);
          if (event.keyCode !== KEY_BACKSPACE) {
            mention += charPressed;
          }
          this.searchString = mention;
          this.searchTerm.emit(this.searchString);
          this.updateSearchList();
        }
      }
    }
  }

  updateSearchList() {
    if (this.selectedItem) {
      if (this.selectedItem.token) {
        this.items = this.selectedItem.schema.attributes;
      }
    }
    let matches: any[] = [];
    if (this.items) {
      let objects = this.items;
      // disabling the search relies on the async operation to do the filtering
      if (!this.disableSearch && this.searchString) {
        let searchStringLowerCase = this.searchString.toLowerCase();
        objects = this.items.filter(e => e[this.labelKey].toLowerCase().startsWith(searchStringLowerCase));
      }
      matches = objects;
      if (this.maxItems > 0) {
        matches = matches.slice(0, this.maxItems);
      }
    }
    // update the search list
    if (this.searchList && this.searchList.instance) {
      this.searchList.instance.items = matches;
      this.searchList.instance.hidden = matches.length === 0;
    }
  }

  showSearchList(nativeElement: HTMLInputElement, charPressed) {
    if (charPressed === this.secondTrigger && !this.selectedItem) {
      return;
    }
    if (this.selectedItem && charPressed === this.secondTrigger) {
      if (!this.selectedItem.token) {
        this.selectedItem = null;
        this.items = this.ruleService.getSavedThings();
        return;
      }
    }
    if (this.searchList) {
      this.searchList.destroy();
      delete this.searchList;
    }
    if (charPressed === this.secondTrigger) {
      this.items = this.selectedItem.schema.attributes;
    } else if (charPressed === this.triggerChar) {
      this.items = this.ruleService.getSavedThings();
    }
    let options = {
      condition: this.items,
      onSave: (event): void => {this.callback(event); }
    };
    if (!this.searchList) {
      const componentFactory: ComponentFactory<MentionListComponent> =
        this.cfr.resolveComponentFactory(MentionListComponent);
      const binding: ResolvedReflectiveProvider[] = ReflectiveInjector.resolve([{
        provide: SetConditionOption,
        useValue: options
      }]);
      const contextInjector: Injector = this.viewContainerRef.parentInjector;
      const childInjector: Injector = ReflectiveInjector.fromResolvedProviders(binding, contextInjector);
      this.searchList = this.viewContainerRef.createComponent(
        componentFactory,
        this.viewContainerRef.length,
        childInjector
      );
      this.searchList.instance.position(nativeElement, this.iframe);
      this.searchList.instance.labelKey = this.labelKey;
    } else {
      this.searchList.instance.activeIndex = 0;
      this.searchList.instance.position(nativeElement, this.iframe);
      window.setTimeout(() => this.searchList.instance.resetScroll());
    }
  }
  private callback(item) {
        this.selectedItem = item;
      this._element.nativeElement.focus();
      let fakeKeydown = {'keyCode': KEY_ENTER, 'wasClick': true};
      this.keyHandler(fakeKeydown, this._element.nativeElement);
  }
}




// WEBPACK FOOTER //
// ./src/app/rules/directives/thing-selector.directive.ts